import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarRange, Loader2, Plus, Sparkles, Target, Zap, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState, LoadingBlock } from "@/components/common/States";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import api, { humanError } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";

const NETWORKS = [
  { code: "instagram", label: "Instagram" },
  { code: "linkedin", label: "LinkedIn" },
  { code: "facebook", label: "Facebook" },
  { code: "x", label: "X" },
];
const CADENCES = [
  { code: "daily", label: "Daily", hint: "Every day in range" },
  { code: "three_per_week", label: "3× per week", hint: "Mon · Wed · Fri" },
  { code: "weekly", label: "Weekly", hint: "Same weekday each week" },
];

const STATUS_TONE = {
  generating: "bg-[#EEF3FB] text-[#1747B0]",
  ready: "bg-[#E8FBF1] text-[#0B5D3C]",
  partial: "bg-[#FFF7E6] text-[#8A6D00]",
  failed: "bg-[#FFE6E6] text-[#B02328]",
};

function planDates(start, end, cadence) {
  if (!start || !end) return [];
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  if (e < s) return [];
  const out = [];
  const d = new Date(s);
  while (d <= e) {
    if (cadence === "daily") out.push(new Date(d));
    else if (cadence === "weekly" && (out.length === 0 || Math.floor((d - out[0]) / 86400000) % 7 === 0)) out.push(new Date(d));
    else if (cadence === "three_per_week" && [1, 3, 5].includes(d.getDay())) out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

export default function Campaigns() {
  const { role } = useAuth();
  const canWrite = can(role, "post.write");
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);

  async function load() {
    try { const { data } = await api.get("/campaigns"); setItems(data.items || []); }
    catch { setItems([]); }
  }
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader
        title={<span className="inline-flex items-center gap-2">Campaigns <Zap className="h-6 w-6 text-[#1F5FD8]" /></span>}
        subtitle="Turn one brief into a week — or a quarter — of ready-to-approve posts."
        actions={canWrite ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="campaign-new-button" className="hs-btn-primary text-sm">
                <Plus className="mr-1 -mt-0.5 inline h-4 w-4" /> New campaign
              </button>
            </DialogTrigger>
            <NewCampaignDialog onCreated={() => { setOpen(false); load(); }} onCancel={() => setOpen(false)} />
          </Dialog>
        ) : null}
      />

      {items === null ? <LoadingBlock rows={4} /> :
        items.length === 0 ? (
          <div className="rounded-xl border border-[#E4E9EF] bg-white p-10">
            <EmptyState
              icon={Zap}
              title="No campaigns yet"
              body="One brief in. A whole series of on-brand drafts out — ready for approvals and the calendar."
              action={canWrite ? (
                <button onClick={() => setOpen(true)} className="hs-btn-primary text-sm" data-testid="campaign-empty-cta">
                  <Sparkles className="mr-1 -mt-0.5 inline h-4 w-4" /> Generate first campaign
                </button>
              ) : null}
            />
          </div>
        ) : (
          <ul className="grid gap-4 md:grid-cols-2" data-testid="campaigns-list">
            {items.map((c) => (
              <li key={c.id}>
                <Link to={`/app/campaigns/${c.id}`} className="group block rounded-xl border border-[#E4E9EF] bg-white p-5 transition-transform hover:-translate-y-0.5 hs-card-shadow" data-testid={`campaign-row-${c.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="line-clamp-2 font-display text-lg font-semibold text-[#0B2B33] group-hover:text-[#1F5FD8]">{c.goal}</div>
                      <div className="mt-1 text-xs text-[#8A97A0]">
                        {c.start_date} → {c.end_date} · {c.cadence?.replace(/_/g, " ")}
                      </div>
                    </div>
                    <span className={`hs-chip ${STATUS_TONE[c.status] || "bg-[#F6F8FA]"}`}>{c.status}</span>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-1">
                    {(c.networks || []).map((n) => <span key={n} className="hs-chip capitalize">{n}</span>)}
                  </div>
                  <div className="mt-4 flex items-center justify-between text-xs text-[#43535E]">
                    <span>{(c.post_ids || []).length} drafts</span>
                    {(c.failed_slots || []).length ? <span className="text-[#B02328]">{c.failed_slots.length} failed</span> : <span className="text-[#16A46B]">All generated</span>}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )
      }
    </div>
  );
}

function NewCampaignDialog({ onCreated, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const twoWeeks = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
  const [goal, setGoal] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(twoWeeks);
  const [networks, setNetworks] = useState(["instagram", "linkedin"]);
  const [cadence, setCadence] = useState("three_per_week");
  const [brandVoice, setBrandVoice] = useState("");
  const [applyInsights, setApplyInsights] = useState(true);
  const [busy, setBusy] = useState(false);

  const dates = useMemo(() => planDates(startDate, endDate, cadence), [startDate, endDate, cadence]);
  const total = dates.length * networks.length;
  const nav = useNavigate();

  function toggleNet(n) {
    setNetworks(ns => ns.includes(n) ? ns.filter(x => x !== n) : [...ns, n]);
  }

  async function create() {
    if (!goal.trim()) return toast.error("Add a goal to start");
    if (!networks.length) return toast.error("Pick at least one network");
    if (!dates.length) return toast.error("No dates fall in that range for this cadence");
    if (total > 120) return toast.error(`That would generate ${total} posts — narrow the range, cadence or networks (max 120)`);
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns", {
        goal: goal.trim(), start_date: startDate, end_date: endDate,
        networks, cadence, brand_voice: brandVoice, apply_insights: applyInsights,
      });
      toast.success(`Generated ${(data.post_ids || []).length} drafts`);
      onCreated?.();
      nav(`/app/campaigns/${data.id}`);
    } catch (e) {
      toast.error(humanError(e));
    } finally { setBusy(false); }
  }

  return (
    <DialogContent className="max-w-2xl" data-testid="new-campaign-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#EEF3FB] text-[#1F5FD8]"><Sparkles className="h-4 w-4" /></span>
          Generate a campaign
        </DialogTitle>
        <DialogDescription>
          One brief in → one draft per date per network, landed straight in your approvals queue.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div>
          <label className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]"><Target className="h-3 w-3" /> Goal / brief</label>
          <textarea
            data-testid="campaign-goal-input"
            value={goal} onChange={(e) => setGoal(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="e.g. Launch our Diwali Festive Hamper. Highlight handcrafted gifting, free delivery over ₹999, limited edition for Oct 20 – Nov 10. Warm, inviting tone."
            className="w-full rounded-lg border border-[#E4E9EF] p-3 text-sm focus:border-[#1F5FD8] focus:outline-none focus:ring-2 focus:ring-[#1F5FD8]/20"
          />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]"><CalendarRange className="h-3 w-3" /> Start</label>
            <input data-testid="campaign-start-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]"><CalendarRange className="h-3 w-3" /> End</label>
            <input data-testid="campaign-end-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm" />
          </div>
        </div>

        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Networks</div>
          <div className="flex flex-wrap gap-1">
            {NETWORKS.map(n => (
              <button key={n.code} onClick={() => toggleNet(n.code)} data-testid={`campaign-network-${n.code}`}
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                  networks.includes(n.code) ? "border-[#1F5FD8] bg-[#EEF3FB] text-[#1F5FD8]" : "border-[#E4E9EF] text-[#43535E] hover:bg-[#F6F8FA]"
                }`}
              >{n.label}</button>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Cadence</div>
          <div className="grid gap-2 md:grid-cols-3">
            {CADENCES.map(c => (
              <button key={c.code} onClick={() => setCadence(c.code)} data-testid={`campaign-cadence-${c.code}`}
                className={`rounded-lg border px-3 py-2 text-left ${
                  cadence === c.code ? "border-[#1F5FD8] bg-[#EEF3FB]" : "border-[#E4E9EF] hover:bg-[#F6F8FA]"
                }`}
              >
                <div className="text-sm font-semibold text-[#0B2B33]">{c.label}</div>
                <div className="text-[11px] text-[#8A97A0]">{c.hint}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-lg bg-[#F6F8FA] p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Estimate</div>
              <div className="mt-1 text-sm text-[#0B2B33]">
                <span className="font-semibold">{dates.length}</span> dates · <span className="font-semibold">{networks.length}</span> networks
                → <span className="font-semibold text-[#1F5FD8]">{total}</span> drafts
              </div>
            </div>
            {total > 0 ? (
              <div className="flex -space-x-1" data-testid="campaign-date-preview">
                {dates.slice(0, 10).map((d, i) => (
                  <div key={i} className="h-6 w-6 rounded-full border-2 border-white bg-[#1F5FD8]/15 text-[10px] font-bold leading-5 text-center text-[#1F5FD8]">
                    {d.getDate()}
                  </div>
                ))}
                {dates.length > 10 ? <div className="h-6 w-6 rounded-full border-2 border-white bg-[#F6F8FA] text-[10px] font-bold leading-5 text-center text-[#43535E]">+{dates.length - 10}</div> : null}
              </div>
            ) : null}
          </div>
        </div>

        <div>
          <label className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Brand voice (optional)</label>
          <input data-testid="campaign-brand-voice" value={brandVoice} onChange={(e) => setBrandVoice(e.target.value)} className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm" placeholder="warm, clear, no hype" />
        </div>

        <div className="flex items-start justify-between gap-3 rounded-lg bg-[#E8FBF1]/40 p-3">
          <div>
            <div className="flex items-center gap-1 text-sm font-semibold text-[#0B2B33]">
              <Sparkles className="h-3 w-3 text-[#1F5FD8]" /> Apply Learning Loop insights
            </div>
            <div className="text-[11px] text-[#43535E]">Fold what works from your past posts into every draft in this campaign.</div>
          </div>
          <Switch checked={applyInsights} onCheckedChange={setApplyInsights} data-testid="campaign-insights-toggle" />
        </div>
      </div>

      <DialogFooter>
        <button onClick={onCancel} className="rounded-full border border-[#E4E9EF] px-4 py-2 text-sm">Cancel</button>
        <button
          onClick={create}
          disabled={busy || total === 0}
          data-testid="campaign-create-confirm"
          className="hs-btn-primary text-sm disabled:opacity-60"
        >
          {busy ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Generating…</span> :
            <>Generate {total || "—"} drafts</>}
        </button>
      </DialogFooter>
    </DialogContent>
  );
}
